import { useQuery, type UseQueryOptions } from '@tanstack/react-query'

import { useDevicesApi } from '../api/context'
import { DEVICE_QUERY_KEYS } from '../api/endpoints'
import type {
  DevicesListParams,
  PaginatedDevicesResponse,
} from '../types/api.types'
import type { ProvisioningDeviceDetail } from '../types/device.types'
import { toApiError } from '../utils/formatApiError'

export function useGetDevices(
  pbxId: number,
  params: DevicesListParams = {},
  options?: Omit<
    UseQueryOptions<PaginatedDevicesResponse, Error, PaginatedDevicesResponse>,
    'queryKey' | 'queryFn'
  >,
) {
  const api = useDevicesApi()

  return useQuery({
    queryKey: DEVICE_QUERY_KEYS.list(pbxId, params as Record<string, unknown>),
    queryFn: async () => {
      const response = await api.getDevices({
        pbx_id: pbxId,
        limit: params.limit,
        offset: params.offset,
        search: params.search,
        ordering: params.ordering,
        manufacturer_id: params.manufacturer_id,
        device_model: params.device_model,
        firmware_version: params.firmware_version,
        warranty: params.warranty,
        transport: params.transport,
        jitter: params.jitter,
        sidecar_enabled: params.sidecar_enabled,
        provisioning: params.provisioning,
      })
      if (!response.ok) {
        throw toApiError(response.data, 'Failed to fetch devices')
      }
      const data = response.data as PaginatedDevicesResponse | undefined
      return {
        results: data?.results ?? [],
        count: data?.count ?? 0,
      }
    },
    enabled: !!pbxId && pbxId > 0,
    retry: false,
    ...options,
  })
}

export function useGetDevice(
  pbxId: number,
  deviceId: number,
  options?: Omit<
    UseQueryOptions<ProvisioningDeviceDetail, Error, ProvisioningDeviceDetail>,
    'queryKey' | 'queryFn'
  >,
) {
  const api = useDevicesApi()

  return useQuery({
    queryKey: DEVICE_QUERY_KEYS.detail(pbxId, deviceId),
    queryFn: async () => {
      const response = await api.getDevice(pbxId, deviceId)
      if (!response.ok) {
        throw toApiError(response.data, `Failed to fetch device ${deviceId}`)
      }
      return response.data as ProvisioningDeviceDetail
    },
    enabled: !!pbxId && pbxId > 0 && !!deviceId && deviceId > 0,
    retry: false,
    ...options,
  })
}
